import React, { createContext, useContext, useState, useEffect } from "react";
import * as api from "../api/educentialApi";

const AuthContext = createContext(null);

// Native helper to safely parse JWT payload
const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("JWT Decode error:", e);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage token on load
  useEffect(() => {
    try {
      const savedToken = localStorage.getItem("educential_token");
      if (savedToken) {
        const payload = parseJwt(savedToken);
        if (payload && payload.exp * 1000 > Date.now()) {
          setToken(savedToken);
          setUser({
            id: payload.id,
            email: payload.email,
            role: payload.role,
          });
          api.setAuthToken(savedToken);
        } else {
          // Token expired or invalid
          localStorage.removeItem("educential_token");
          api.clearAuthToken();
        }
      }
    } catch (e) {
      console.warn("Auth initialization error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle Login
  const login = async (email, password) => {
    try {
      const response = await api.login({ email, password });
      if (response && response.accessToken) {
        const savedToken = response.accessToken;
        setToken(savedToken);
        const payload = parseJwt(savedToken);
        const userData = {
          id: payload.id,
          email: payload.email,
          role: payload.role,
        };
        setUser(userData);
        return { success: true, user: userData };
      }
      throw new Error("Invalid server response format.");
    } catch (err) {
      console.error("Login failed:", err);
      const errMsg = err.response?.data?.error || err.message || "Invalid email or password.";
      return { success: false, error: errMsg };
    }
  };

  // Handle Sign Up
  const signup = async (email, password, role = 'STUDENT') => {
    try {
      await api.signup({ email, password, role });
      return { success: true };
    } catch (err) {
      console.error("Signup failed:", err);
      const errMsg = err.response?.data?.error || err.message || "Failed to create account.";
      return { success: false, error: errMsg };
    }
  };

  // Handle Logout
  const logout = () => {
    setToken(null);
    setUser(null);
    api.clearAuthToken();
    localStorage.removeItem("educential_token");
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
